// src/components/job-engine/useJobEvents.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { getJobEvents } from "@/api/jobEngine";
import type { JobEvent } from "@/api/jobEngine";

export interface UseJobEventsResult {
  items: JobEvent[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useJobEvents(jobId: string): UseJobEventsResult {
  const [items, setItems] = useState<JobEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!jobId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    setError(null);
    getJobEvents(jobId, ctrl.signal)
      .then((r) => { if (!ctrl.signal.aborted) setItems(r ?? []); })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (isAxiosError(err)) setError(err.response?.data?.title ?? "Failed to load events");
        else setError("An unexpected error occurred");
      })
      .finally(() => { if (!ctrl.signal.aborted) setIsLoading(false); });
    return () => ctrl.abort();
  }, [jobId, tick]);

  return { items, isLoading, error, refetch };
}
